import { CACHE_MAX_AGE_DEFAULT, CACHE_MAX_AGE_DOWNLOAD } from 'common/constants';
import { API_URL } from 'common/shared';

import {
    clearReduxStore,
    deleteEnableFeatures, deleteToken,
    getUser,
    setEnableFeatures,
    setToken
} from 'redux/actions';
import { store } from 'redux/store';

class APIUtils {
    static _cache = {}; // In-memory cache
    static broadcastChannel = new BroadcastChannel('bo_channel');

    static POSTnoAuth(endpoint, data) {
        return fetch(API_URL + endpoint, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data)
        }).then(response => {
            if (response.ok) {
                return response.json();
            }
            return Promise.reject({ message: response.statusText });
        }).then(data => {
            if (data.success) {
                return data;
            }
            return Promise.reject({ message: data.err, data: data.data });
        });
    }

    static POST(endpoint, data, type = '') {
        const token = APIUtils.getToken();
        const maxAge = (
            type === 'csv'
            || type === 'xlsx'
            || type === 'whatsapp/xlsx'
            || type === 'bulk_download'
        ) ? CACHE_MAX_AGE_DOWNLOAD : CACHE_MAX_AGE_DEFAULT;

        const headers = {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        };

        const body = JSON.stringify(data);
        const requestKey = `${endpoint}-${body}`;

        if (maxAge > 0 && APIUtils._cache[requestKey]) {
            return APIUtils._cache[requestKey];
        }

        const request = fetch(API_URL + endpoint, {
            method: 'POST',
            headers,
            body
        }).then(response => {
            if (response.ok) {
                return response.json();
            }
            return Promise.reject({ message: response.statusText });
        }).then(data => {
            if (data.success) {
                return data;
            }

            if (data.err === 'err:ip_not_allowed') {
                APIUtils.logOut(data.err);
            }

            return Promise.reject({ message: data.err, data: data.data });
        });

        // Save to in-memory cache
        if (maxAge > 0) {
            APIUtils._cache[requestKey] = request;

            if (maxAge !== 'infinity') {
                setTimeout(() => {
                    delete APIUtils._cache[requestKey];
                }, maxAge);
            }
        }

        return request;
    }

    static POSTNoFormat(endpoint, data) {
        return fetch(API_URL + endpoint, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: `Bearer ${APIUtils.getToken()}`,
            },
            body: JSON.stringify(data)
        }).then(response => {
            if (response.ok) {
                return response.json();
            }

            if (data.err === 'err:ip_not_allowed') {
                APIUtils.logOut(data.err);
            }

            return Promise.reject({ message: response.statusText });
        }).then(data => {
            return data;
        });
    }

    static POSTFile(endpoint, data) {
        return fetch(API_URL + endpoint, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${APIUtils.getToken()}`,
            },
            body: data
        }).then(response => {
            if (response.ok) {
                return response.json();
            }
            return Promise.reject({ message: response.statusText });
        }).then(data => {
            if (data.success) {
                return data;
            }

            if (data.err === 'err:ip_not_allowed') {
                APIUtils.logOut(data.err);
            }

            return Promise.reject({ message: data.err, data: data.data });
        });
    }

    static GET(endpoint) {
        return fetch(API_URL + endpoint, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${APIUtils.getToken()}`,
            },
        }).then(response => {
            if (response.ok) {
                return response.json();
            }
            return Promise.reject({ message: response.statusText });
        }).then(data => {
            if (data.success) {
                return data;
            }

            if (data.err === 'err:ip_not_allowed') {
                APIUtils.logOut(data.err);
            }

            return Promise.reject({ message: data.err, data: data.data });
        });
    }

    static POSTDownload(endpoint) {
        return fetch(API_URL + endpoint, {
            method: 'POST',
            mode: 'no-cors',
            headers: {
                Authorization: `Bearer ${APIUtils.getToken()}`,
                accept: 'application/json',
            },
        }).then(response => response.blob())
            .then(blob => URL.createObjectURL(blob))
            .then(url => {
                window.open(url, '_blank');
                URL.revokeObjectURL(url);
            });
    }

    // ==== Auth and State Management ====

    static logOut(reason) {
        this.deleteToken();
        this.deleteReduxStorage();
        this.deleteEnableFeatures();
        this.deleteUserInformation();
        this.broadcastChannel.postMessage('bo-logout');

        if (reason) {
            this.saveLogOutReason(reason);
        }
    }

    static saveToken(token) {
        store.dispatch(setToken(token));
    }

    static saveUserInfomation(user) {
        localStorage.setItem('user', JSON.stringify(user));
        store.dispatch(getUser(user));
        this.broadcastChannel.postMessage('bo-login');
    }

    static deleteUserInformation() {
        localStorage.removeItem('user');
    }

    static getToken() {
        return store.getState().auth.token;
    }

    static deleteToken() {
        store.dispatch(deleteToken());
    }

    static deleteReduxStorage() {
        store.dispatch(clearReduxStore());
    }

    static saveEnableFeatures(data) {
        store.dispatch(setEnableFeatures(data));
    }

    static getEnableFeatures() {
        return store.getState().auth.enable_features;
    }

    static deleteEnableFeatures() {
        store.dispatch(deleteEnableFeatures());
    }

    static saveLogOutReason(reason) {
        localStorage.setItem('logOutReason', reason);
    }

    static getLogOutReason() {
        return localStorage.getItem('logOutReason');
    }

    static deleteLogOutReason() {
        return localStorage.removeItem('logOutReason');
    }

    static isAuthed() {
        return APIUtils.getToken();
    }
}

export default APIUtils;
